import { useState } from 'react';
import { ArrowLeft, ArrowRight, Clock, KeyRound, Lock, Mail, ShieldCheck } from 'lucide-react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { CountdownTimer } from '@/components/ui/CountdownTimer';
import { OtpInput } from '@/components/ui/OtpInput';
import { AuthHero } from '@/components/common/AuthHero';
import { SectionCard } from '@/components/common/SectionCard';
import { ShieldNote } from '@/components/common/ShieldNote';
import { useVerifyEmail } from '@/hooks/useVerifyEmail';
import { useResendOtp } from '@/hooks/useResendOtp';
import { useCountdown } from '@/hooks/useCountdown';
import { OTP_UI } from '@/lib/constants/otp';
import { API_ERROR_CODES } from '@/lib/constants/error-codes';
import { getApiErrorCode, mapApiError } from '@/lib/utils/error.utils';
import { IssueReportJourneySteps } from '@/components/common/IssueReportJourneySteps';
import { peekPurchaseIntent } from '@/lib/auth/intent-storage';
import { journeyRegisterPath } from '@/lib/auth/post-auth-landing';

interface VerifyLocationState {
  email?: string;
  /** False when the signup OTP email failed to send (steer user to Resend). */
  otpEmailSent?: boolean;
}

const VERIFY_ERROR_BY_CODE: Record<string, string> = {
  [API_ERROR_CODES.OTP_INVALID]: UI_STRINGS.VERIFY_EMAIL.ERROR_INVALID,
  [API_ERROR_CODES.OTP_NOT_FOUND]: UI_STRINGS.VERIFY_EMAIL.ERROR_INVALID,
  [API_ERROR_CODES.OTP_EXPIRED]: UI_STRINGS.VERIFY_EMAIL.ERROR_EXPIRED,
  [API_ERROR_CODES.OTP_LOCKED]: UI_STRINGS.VERIFY_EMAIL.ERROR_LOCKED,
};

const RESEND_ERROR_BY_CODE: Record<string, string> = {
  [API_ERROR_CODES.RATE_LIMITED]: UI_STRINGS.VERIFY_EMAIL.ERROR_RATE_LIMITED,
};

/** Codes after which waiting out the cooldown makes no sense - resend now. */
const RESEND_UNLOCK_CODES = new Set<string>([
  API_ERROR_CODES.OTP_EXPIRED,
  API_ERROR_CODES.OTP_LOCKED,
]);

export function VerifyEmailPage() {
  const location = useLocation();
  const email = (location.state as VerifyLocationState | null)?.email;

  const verify = useVerifyEmail();
  const resend = useResendOtp();

  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string>();
  // Remounts the expiry countdown when a new code is sent.
  const [timerKey, setTimerKey] = useState(0);
  // Resend unlocks after a short cooldown (not the full 10-minute code TTL),
  // or immediately when the server says the code is expired/locked.
  const cooldown = useCountdown(OTP_UI.RESEND_COOLDOWN_SECONDS);
  const verifyErrorCode = getApiErrorCode(verify.error);
  // Signup completed but the OTP email failed to send: warn and unlock Resend.
  const sendFailed =
    (location.state as VerifyLocationState | null)?.otpEmailSent === false &&
    !resend.isSuccess;
  const resendReady =
    cooldown.expired ||
    sendFailed ||
    (verifyErrorCode !== undefined && RESEND_UNLOCK_CODES.has(verifyErrorCode));

  // Arriving without an email means the signup step was skipped (direct nav or
  // refresh dropped the router state) - send the user back to start.
  if (!email) {
    return <Navigate to={journeyRegisterPath()} replace />;
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!new RegExp(`^\\d{${OTP_UI.CODE_LENGTH}}$`).test(code)) {
      setCodeError(`Enter the ${OTP_UI.CODE_LENGTH}-digit code`);
      return;
    }
    setCodeError(undefined);
    verify.mutate({ email, code });
  };

  const handleResend = () => {
    // Cooldown/timer only restart once the resend actually succeeded - a
    // failed resend must not silently re-lock the button for 60s.
    resend.mutate(
      { email },
      {
        onSuccess: () => {
          cooldown.reset();
          setTimerKey((key) => key + 1);
        },
      },
    );
  };

  return (
    <div className="flex flex-col items-center bg-page-bg px-4 py-16">
      {peekPurchaseIntent() && <IssueReportJourneySteps stage="secure" className="mb-10" />}
      <AuthHero
        badgeIcon={<ShieldCheck size={14} />}
        badgeText={UI_STRINGS.VERIFY_EMAIL.BADGE}
        heading={
          <>
            {UI_STRINGS.VERIFY_EMAIL.HEADING_PREFIX}
            <span className="text-green">{UI_STRINGS.VERIFY_EMAIL.HEADING_HIGHLIGHT}</span>
          </>
        }
        subtitleClassName="max-w-2xl"
        subtitle={
          <>
            <p>
              {UI_STRINGS.VERIFY_EMAIL.SUBTITLE_PREFIX}{' '}
              <span className="whitespace-nowrap font-semibold text-navy">{email}</span>
            </p>
            <p className="mt-1 text-sm">{UI_STRINGS.VERIFY_EMAIL.SUBTITLE_LINE2}</p>
          </>
        }
      />

      <div className="mt-10 flex w-full max-w-3xl flex-col gap-6">
        {/* "Code sent to" banner */}
        <div className="flex items-center justify-between gap-4 rounded-card border border-green/30 bg-green/10 px-6 py-4">
          <div className="flex items-center gap-4">
            <span
              aria-hidden="true"
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-green/20 text-dkgreen"
            >
              <Mail size={18} />
            </span>
            <div>
              <p className="font-sans text-[11px] font-semibold uppercase tracking-wide text-dkgreen">
                {UI_STRINGS.VERIFY_EMAIL.SENT_TO_LABEL}
              </p>
              <p className="font-sans text-sm font-semibold text-navy">{email}</p>
              <p className="text-xs text-subtext">{UI_STRINGS.VERIFY_EMAIL.SENT_TO_HINT}</p>
            </div>
          </div>
          {!sendFailed && (
            <Badge variant="green-outline">&bull; {UI_STRINGS.VERIFY_EMAIL.SENT_CHIP}</Badge>
          )}
        </div>

        {sendFailed && (
          <p
            role="alert"
            className="rounded-card border border-warning/30 bg-warning/10 px-6 py-3 text-sm text-warning"
          >
            {UI_STRINGS.VERIFY_EMAIL.SEND_FAILED}
          </p>
        )}

        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
          <SectionCard
            title={UI_STRINGS.VERIFY_EMAIL.CARD_TITLE}
            subtitle={UI_STRINGS.VERIFY_EMAIL.CARD_SUBTITLE}
            icon={<KeyRound size={20} />}
            className="pb-0"
          >
            {verify.isError && (
              <p role="alert" className="mb-4 text-center text-sm text-danger">
                {mapApiError(verify.error, VERIFY_ERROR_BY_CODE, UI_STRINGS.VERIFY_EMAIL.ERROR)}
              </p>
            )}
            {resend.isSuccess && (
              <p role="status" className="mb-4 text-center text-sm text-dkgreen">
                {UI_STRINGS.VERIFY_EMAIL.RESENT}
              </p>
            )}
            {resend.isError && (
              <p role="alert" className="mb-4 text-center text-sm text-danger">
                {mapApiError(
                  resend.error,
                  RESEND_ERROR_BY_CODE,
                  UI_STRINGS.VERIFY_EMAIL.RESEND_ERROR,
                )}
              </p>
            )}

            <div className="flex flex-col items-center gap-4 py-4">
              <OtpInput
                label={UI_STRINGS.VERIFY_EMAIL.CODE_LABEL}
                value={code}
                onChange={setCode}
                length={OTP_UI.CODE_LENGTH}
                error={codeError}
              />
              <p className="flex items-center gap-1.5 text-xs text-slate">
                <Lock size={12} aria-hidden="true" />
                <span>
                  {UI_STRINGS.VERIFY_EMAIL.EXPIRES_NOTE_PREFIX}
                  <span className="font-semibold text-navy">
                    {UI_STRINGS.VERIFY_EMAIL.EXPIRES_NOTE_BOLD}
                  </span>
                  {UI_STRINGS.VERIFY_EMAIL.EXPIRES_NOTE_SUFFIX}
                </span>
              </p>
            </div>

            <div className="-mx-8 mt-4 flex items-center justify-between gap-4 rounded-b-card border-t border-divider bg-page-bg px-8 py-4">
              <div className="flex items-center gap-3">
                <span
                  aria-hidden="true"
                  className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-dkgreen text-dkgreen"
                >
                  <Clock size={16} />
                </span>
                <div className="leading-tight">
                  <p className="text-xs text-slate">{UI_STRINGS.VERIFY_EMAIL.EXPIRES_IN}</p>
                  <CountdownTimer key={timerKey} seconds={OTP_UI.CODE_TTL_SECONDS} />
                </div>
              </div>
              <p className="text-sm text-slate">
                {UI_STRINGS.VERIFY_EMAIL.NO_CODE}{' '}
                <Button
                  type="button"
                  variant="link"
                  disabled={!resendReady || resend.isPending}
                  onClick={handleResend}
                  className="align-baseline disabled:text-slate"
                >
                  {resend.isPending
                    ? UI_STRINGS.VERIFY_EMAIL.RESENDING
                    : UI_STRINGS.VERIFY_EMAIL.RESEND}
                </Button>
              </p>
            </div>
          </SectionCard>

          <SectionCard>
            <ShieldNote className="justify-end">
              {UI_STRINGS.VERIFY_EMAIL.ENCRYPTION_NOTE}
            </ShieldNote>
            <div className="mt-4 flex items-center justify-end gap-6">
              <Link
                to={journeyRegisterPath()}
                className="inline-flex items-center gap-1.5 font-sans text-sm font-medium text-navy hover:text-dkgreen"
              >
                <ArrowLeft size={16} aria-hidden="true" />
                {UI_STRINGS.VERIFY_EMAIL.BACK}
              </Link>
              <Button
                type="submit"
                variant="navy"
                disabled={verify.isPending}
                className="inline-flex items-center gap-2 px-8 py-3.5 text-base drop-shadow-md"
              >
                {verify.isPending
                  ? UI_STRINGS.VERIFY_EMAIL.SUBMITTING
                  : UI_STRINGS.VERIFY_EMAIL.SUBMIT}
                <ArrowRight size={18} aria-hidden="true" />
              </Button>
            </div>
          </SectionCard>
        </form>
      </div>
    </div>
  );
}
